/**
 * Result Type Configuration
 * Maps test codes to their result types and allowed values for dynamic form rendering
 */

import type { ResultType } from "~/services/api/domains/pending/pending.types";

export interface ResultTypeConfig {
  resultType: ResultType;
  allowedValues?: string[];
}

/**
 * Test code to result type mapping
 * Add test codes here to enable enum/text editing
 */
export const RESULT_TYPE_CONFIG: Record<string, ResultTypeConfig> = {
  // Demo test codes for testing different result types

  // FT 4 (10195) - Enum type with predefined values
  "10195": {
    resultType: "enum",
    allowedValues: ["7.0", "9.10", "10.0", "12.5", "15.0", "15.3", "17.5", "20.0", "22.5"],
  },

  // Cholesterol (20411) - Text type for free-form comments
  "20411": {
    resultType: "text",
  },
};

/**
 * Get result type configuration for a test code
 * Defaults to "numeric" if not found in config
 */
export function getResultTypeConfig(testCode: string): ResultTypeConfig | null {
  const normalizedCode = testCode.toUpperCase();
  return RESULT_TYPE_CONFIG[normalizedCode] || null;
}

/**
 * Check if a test code is an enum type
 */
export function isEnumType(testCode: string): boolean {
  const config = getResultTypeConfig(testCode);
  return config?.resultType === "enum";
}

/**
 * Check if a test code is a text type
 */
export function isTextType(testCode: string): boolean {
  const config = getResultTypeConfig(testCode);
  return config?.resultType === "text";
}
